import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useUserState } from '../../../providers/user-state.provider.ts';
import { refreshUserInfo } from '../../../services/user.service';
import { completeSubscription } from '../../../services/payment.service';
import { subscriptionGrantsAccess } from '../../../utils/subscription';
import { getPaymentErrorMessage } from '../../../utils/payment-error';
import type { Subscription } from '../../../types/payment.types';
import { trackMarketingConversion } from '../../../services/marketing.service';
import { createAnalyticsClientEventId } from '../../../services/analytics.service';
import { useSubscriptionStatus } from '../../../providers/subscription-status.provider';
import { usePageActivationRefresh } from '../../../hooks/usePageActivationRefresh';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clearJoinCheckoutAttempt, getSafeReturnTo } from '../../../utils/join';
import { useSaveVideo } from '../../../hooks/useSaveVideo';

const COMPLETION_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 16000];

const delay = (milliseconds: number) => new Promise((resolve) => setTimeout(resolve, milliseconds));

const readLocalStorageItem = (key: string): string | null => {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
};

const removeLocalStorageItem = (key: string) => {
    try {
        window.localStorage.removeItem(key);
    } catch {
        // Successful subscription verification must not depend on browser storage availability.
    }
};

const getPendingSubscriptionMessage = (subscription: Subscription | null): string => {
    const status = subscription?.PlanStatus?.trim();
    return status
        ? `Subscription verification returned status "${status}", but access is not active yet.`
        : 'We could not verify this subscription yet. Use Check again or contact support.';
};

const PaymentCompleted = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const returnTo = getSafeReturnTo(searchParams.get('returnTo'));
    const { isUserStateLoading, setUserInfo, getUserInfo } = useUserState();
    const { refreshSubscription } = useSubscriptionStatus();
    const { saveVideo } = useSaveVideo();
    const renderedUser = getUserInfo();
    const authToken = renderedUser?.token ?? '';
    const accountKey = renderedUser?.userId ?? authToken;
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isVerified, setIsVerified] = useState(false);
    const [isPending, setIsPending] = useState(true);
    const [verificationAccountKey, setVerificationAccountKey] = useState(accountKey);
    const isMountedRef = useRef(true);
    const isVerifyingRef = useRef(false);
    const isVerifiedRef = useRef(false);
    const subscriptionEventIdRef = useRef(createAnalyticsClientEventId());
    const hasTrackedConversionRef = useRef(false);
    const authTokenRef = useRef(authToken);
    const accountKeyRef = useRef(accountKey);
    // Invalidate an in-flight verification synchronously when the authenticated session changes.
    if (authTokenRef.current !== authToken) {
        authTokenRef.current = authToken;
        isVerifyingRef.current = false;
    }
    if (accountKeyRef.current !== accountKey) {
        accountKeyRef.current = accountKey;
        isVerifyingRef.current = false;
        isVerifiedRef.current = false;
        subscriptionEventIdRef.current = createAnalyticsClientEventId();
        hasTrackedConversionRef.current = false;
    }

    const isRunCurrent = useCallback((runToken: string, runAccountKey: string) => (
        isMountedRef.current
        && authTokenRef.current === runToken
        && accountKeyRef.current === runAccountKey
    ), []);

    const verifySubscription = useCallback(async () => {
        if (isUserStateLoading || isVerifyingRef.current || isVerifiedRef.current) {
            return;
        }

        const currentUser = getUserInfo();
        const token = authToken;
        const runAccountKey = currentUser?.userId ?? token;
        if (currentUser?.token !== token) {
            return;
        }
        if (!token) {
            if (isMountedRef.current) {
                setVerificationAccountKey(runAccountKey);
                setErrorMessage('We could not verify this subscription yet. Use Check again or contact support.');
                setIsPending(false);
            }
            return;
        }

        isVerifyingRef.current = true;
        if (isRunCurrent(token, runAccountKey)) {
            setVerificationAccountKey(runAccountKey);
            setIsPending(true);
        }

        try {
            let lastFailureMessage = 'We could not verify this subscription yet. Use Check again or contact support.';
            for (let attempt = 0; attempt <= COMPLETION_RETRY_DELAYS_MS.length; attempt += 1) {
                try {
                    const subscription = await completeSubscription(token, subscriptionEventIdRef.current);
                    if (!isRunCurrent(token, runAccountKey)) {
                        return;
                    }

                    if (subscriptionGrantsAccess(subscription)) {
                        await refreshSubscription();
                        if (!isRunCurrent(token, runAccountKey)) {
                            return;
                        }

                        if (!hasTrackedConversionRef.current) {
                            hasTrackedConversionRef.current = true;
                            trackMarketingConversion({
                                kind: 'subscribe',
                                eventId: subscriptionEventIdRef.current,
                                metadata: {
                                    source: 'payment_completed',
                                    interval: subscription.PlanInterval ?? ''
                                }
                            });
                        }

                        let updatedUser: Awaited<ReturnType<typeof refreshUserInfo>> | null = null;
                        try {
                            updatedUser = await refreshUserInfo(token);
                        } catch {
                            console.error('Failed to refresh user info after subscription verification');
                        }
                        if (!isRunCurrent(token, runAccountKey)) {
                            return;
                        }

                        const pendingSavedVideoId = readLocalStorageItem('pendingSavedVideoId');
                        if (pendingSavedVideoId) {
                            try {
                                const wasSaved = await saveVideo(pendingSavedVideoId, updatedUser?.token ?? token);
                                if (wasSaved) {
                                    removeLocalStorageItem('pendingSavedVideoId');
                                }
                            } catch {
                                console.error('Failed to fulfill the pending video save after subscription verification');
                            }
                        }
                        if (!isRunCurrent(token, runAccountKey)) {
                            return;
                        }

                        isVerifiedRef.current = true;
                        setVerificationAccountKey(runAccountKey);
                        setIsVerified(true);
                        setErrorMessage(null);
                        if (updatedUser) {
                            setUserInfo(updatedUser);
                        }
                        return;
                    }
                    lastFailureMessage = getPendingSubscriptionMessage(subscription);
                } catch (completionError) {
                    lastFailureMessage = getPaymentErrorMessage(completionError);
                }

                if (attempt < COMPLETION_RETRY_DELAYS_MS.length) {
                    await delay(COMPLETION_RETRY_DELAYS_MS[attempt]);
                    if (!isRunCurrent(token, runAccountKey)) {
                        return;
                    }
                }
            }

            if (isRunCurrent(token, runAccountKey)) {
                setErrorMessage(lastFailureMessage);
            }
        } catch (error: unknown) {
            if (isRunCurrent(token, runAccountKey)) {
                setErrorMessage(getPaymentErrorMessage(error));
            }
            console.error('Failed to refresh user info');
        } finally {
            if (isRunCurrent(token, runAccountKey)) {
                isVerifyingRef.current = false;
                setIsPending(false);
            }
        }
    }, [authToken, getUserInfo, isRunCurrent, isUserStateLoading, refreshSubscription, saveVideo, setUserInfo]);

    useEffect(() => {
        isMountedRef.current = true;
        void verifySubscription();
        return () => {
            isMountedRef.current = false;
        };
    }, [verifySubscription]);

    const hasCurrentAccountState = verificationAccountKey === accountKey;
    const currentIsVerified = hasCurrentAccountState ? isVerified : false;
    const currentIsPending = hasCurrentAccountState ? isPending : !!authToken;
    const currentErrorMessage = hasCurrentAccountState ? errorMessage : null;

    usePageActivationRefresh(!currentIsVerified && !isUserStateLoading, verifySubscription);

    useEffect(() => {
        if (!currentIsVerified) return;
        clearJoinCheckoutAttempt();
        removeLocalStorageItem('pendingVideoPurchaseId');
        removeLocalStorageItem('redirectAfterLogin');
        navigate(returnTo, { replace: true });
    }, [currentIsVerified, navigate, returnTo]);
    
    return (
        <div className="flex flex-col items-center p-6 h-screen bg-gray-100">
            <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-6">
                <h1 className="text-3xl font-semibold mb-6 text-center text-gray-800">Payment Completed</h1>
                <p className="text-center text-lg text-gray-700 mb-4">
                    {currentIsVerified
                        ? 'Thank you for your purchase! Your subscription has been successfully activated.'
                        : currentIsPending
                            ? 'Thank you for your purchase. We are verifying your subscription access.'
                            : currentErrorMessage
                                ? 'Thank you for your purchase. We are still verifying your subscription access.'
                                : 'Thank you for your purchase. We are checking your subscription access.'}
                </p>
                <p className="text-center text-lg text-gray-700">
                    {currentIsVerified
                        ? 'You can now access all the premium features of your plan.'
                        : currentIsPending
                            ? 'This usually completes within a few seconds.'
                            : currentErrorMessage
                                ? 'Some payment methods complete asynchronously. Use Check again, or contact support if access does not update.'
                                : 'Use Check again if your access does not update shortly.'}
                </p>
                {currentErrorMessage ? (
                    <p className="text-center text-sm text-red-700 mt-4" role="alert">
                        {currentErrorMessage}
                    </p>
                ) : null}
                {!currentIsVerified ? (
                    <button
                        type="button"
                        onClick={() => void verifySubscription()}
                        disabled={currentIsPending}
                        className="mt-6 w-full bg-indigo-100 text-blue-800 font-semibold py-2 px-4 rounded-lg shadow-sm hover:bg-indigo-200 disabled:opacity-60 transition duration-200"
                    >
                        {currentIsPending ? 'Checking...' : 'Check again'}
                    </button>
                ) : null}
                <button
                    onClick={() => navigate(currentIsVerified ? returnTo : '/', { replace: currentIsVerified })}
                    className="mt-6 w-full bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:bg-blue-700 transition duration-200"
                >
                    {currentIsVerified ? 'Start watching' : 'Go to Homepage'}
                </button>
            </div>
        </div>
    );
};

export default PaymentCompleted;
